import React, { useEffect, useRef, useState } from "react";
import {
  DatePicker, Empty, Input, Select, Space, Table, Tooltip,
} from "antd";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { fetchEsvRetestActions, markEsvRetestActionRead, updateEsvRetestActionDataOps } from "../../../actions/dashboard";
import { loadDataOpsAssigneeSelectOptions } from "../DataOpsAnalytics/utils/utils";
import { getSearchOptions } from "../../../actions/batchReports";
import { getUserList } from "../../../actions/sendReports";
import { formatAntdDateRange, getUser } from "../../../utils/helpers";
import "../QC/QCFilterBar.css";
import "../QC/QC.css";
import "./MLRouting/DashboardMlRouting.css";
import EsvActionDetailModal from "./EsvActionDetailModal";
import {
  ESV_CLEARANCE_FILTER_OPTIONS,
  EsvAssignedToCell,
  EsvDataOpsStatusCell,
  EsvReadStatusCell,
  canAssignEsvAction,
  canEditEsvReadStatus,
  canEditEsvStatus,
  mergeEsvAssignedToRow,
  normalizeEsvClearance,
} from "./EsvDataOpsCell";
import EsvBulkActions, { EsvRowActions } from "./EsvBulkActions";
import {
  actionLabelFor,
  displayCell,
  formatActionAt,
  isActionRead,
  readStatusTooltip,
} from "./esvActionDisplay";
import "./EsvRetestActions.css";

const DEFAULT_PAGE_SIZE = 10;
const dateFormat = "MM/DD/YYYY";

const defaultCustomerOption = { value: "", label: "All Customers" };
const defaultCsmOption = { value: "", label: "All" };

const getDefaultDateRange = () => [dayjs(), dayjs()];

const READ_STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "read", label: "Read" },
];

const withTooltip = (label, title) => {
  if (title == null || title === "" || title === "-") return label;
  return <Tooltip title={title}><span>{label}</span></Tooltip>;
};

export default function DashboardEsvRetestActions() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [readStatus, setReadStatus] = useState("");
  const [customerPk, setCustomerPk] = useState("");
  const [csm, setCsm] = useState("");
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [customerOptions, setCustomerOptions] = useState([defaultCustomerOption]);
  const [customerLoading, setCustomerLoading] = useState(true);
  const [csmOptions, setCsmOptions] = useState([defaultCsmOption]);
  const [csmLoading, setCsmLoading] = useState(true);
  const [userNames, setUserNames] = useState({});
  const [markingId, setMarkingId] = useState(null);
  const [markingBulk, setMarkingBulk] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const [listVersion, setListVersion] = useState(0);
  const [clearanceFilter, setClearanceFilter] = useState("");
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [assigneeLoading, setAssigneeLoading] = useState(true);
  const [savingStatusIds, setSavingStatusIds] = useState({});
  const assignInFlightRef = useRef(new Set());
  const statusInFlightRef = useRef(new Set());
  const readInFlightRef = useRef(false);
  const actionRefreshRef = useRef(false);
  const [actionLoading, setActionLoading] = useState(false);
  const canAssign = canAssignEsvAction();
  const canEditReadStatus = canEditEsvReadStatus();
  const canEditStatus = canEditEsvStatus();
  const isMountedRef = useRef(true);
  const containerRef = useRef(null);
  const [tableWidth, setTableWidth] = useState(1000);

  useEffect(() => {
    const computeTableWidth = () => {
      const el = containerRef.current;
      if (!el) return;
      const cs = window.getComputedStyle(el);
      const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      const inner = Math.max(0, el.clientWidth - padX);
      setTableWidth(inner);
    };
    computeTableWidth();
    window.addEventListener("resize", computeTableWidth);
    return () => window.removeEventListener("resize", computeTableWidth);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    const loadCustomers = async () => {
      const response = await getSearchOptions();
      if (!isMountedRef.current) return;
      const options = [defaultCustomerOption];
      if (response?.success && Array.isArray(response.list)) {
        response.list.forEach((item) => {
          if (item?.customer_id == null || String(item.customer_id).trim() === "") return;
          const company = item.company != null ? String(item.company) : "";
          const accountId = String(item.customer_id);
          options.push({
            value: accountId,
            label: company ? `${company} (${accountId})` : accountId,
          });
        });
      } else if (response?.message) {
        toast.info(response.message);
      }
      setCustomerOptions(options);
      setCustomerLoading(false);
    };
    const loadCsmUsers = async () => {
      const response = await getUserList();
      if (!isMountedRef.current) return;
      const options = [defaultCsmOption, { value: -1, label: "Unassigned" }];
      const names = {};
      if (response?.success && Array.isArray(response.results)) {
        response.results.forEach((item) => {
          if (item?.id == null) return;
          names[item.id] = `${item.first_name || ""} ${item.last_name || ""}`.trim();
          if (![4, 10].includes(item.role)) return;
          options.push({
            value: item.id,
            label: names[item.id],
          });
        });
      }
      setUserNames(names);
      setCsmOptions(options);
      setCsmLoading(false);
    };
    const loadAssignees = async () => {
      const response = await loadDataOpsAssigneeSelectOptions();
      if (!isMountedRef.current) return;
      if (!response.success) {
        toast.error(response.message || "Failed to load Data Ops users");
        setAssigneeOptions([]);
      } else {
        setAssigneeOptions(response.options || []);
      }
      setAssigneeLoading(false);
    };
    loadCustomers();
    loadCsmUsers();
    loadAssignees();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const loadActions = async () => {
      const fromAction = actionRefreshRef.current;
      if (!fromAction) setLoading(true);
      setSelectedRowKeys([]);
      const params = {
        page,
        page_size: pageSize,
      };
      if (customerPk !== "" && customerPk != null) {
        params.customer_id = customerPk;
      }
      if (search) {
        params.search = search;
      }
      if (readStatus === "read" || readStatus === "unread") {
        params.status = readStatus;
      }
      const formattedDateRange = formatAntdDateRange(dateRange);
      if (formattedDateRange) {
        params.date_range = formattedDateRange;
      }
      if (csm !== "" && csm != null) {
        params.csm = csm;
      }
      if (clearanceFilter === "pending" || clearanceFilter === "cleared") {
        params.dataops_status = clearanceFilter;
      }

      const response = await fetchEsvRetestActions(params, controller.signal);
      if (!active) return;

      if (response.canceled) {
        if (!fromAction) setLoading(false);
        return;
      }

      if (response.success === false) {
        setRows([]);
        setTotal(0);
        toast.error(response.message || "Failed to fetch ESV retest actions");
        setLoading(false);
        if (fromAction) {
          actionRefreshRef.current = false;
          setActionLoading(false);
          setMarkingId(null);
          setMarkingBulk(false);
          statusInFlightRef.current.clear();
          setSavingStatusIds({});
        }
        return;
      }

      const nextRows = Array.isArray(response.results) ? response.results : [];
      setRows(nextRows);
      setTotal(Number(response.count) || 0);
      setDetailId((currentId) => (
        currentId != null && nextRows.some((row) => row.id === currentId) ? currentId : null
      ));
      setLoading(false);
      if (fromAction) {
        actionRefreshRef.current = false;
        setActionLoading(false);
        setMarkingId(null);
        setMarkingBulk(false);
        statusInFlightRef.current.clear();
        setSavingStatusIds({});
      }
    };

    loadActions();
    return () => {
      active = false;
      controller.abort();
    };
  }, [customerPk, csm, page, pageSize, search, readStatus, dateRange, clearanceFilter, listVersion]);

  const applySearch = (value) => {
    const nextValue = String(value || "").trim();
    setSearchDraft(nextValue);
    if (nextValue === search) return;
    setSearch(nextValue);
    setPage(1);
  };

  const handleReadStatusChange = (value) => {
    setReadStatus(value || "");
    setPage(1);
  };

  const handleClearanceFilterChange = (value) => {
    setClearanceFilter(value ?? "");
    setPage(1);
  };

  const trackFieldSaving = (inFlightRef, setSavingIds, id, active) => {
    if (active) {
      inFlightRef.current.add(id);
      setSavingIds((current) => ({ ...current, [id]: true }));
      return;
    }
    inFlightRef.current.delete(id);
    setSavingIds((current) => {
      if (!current[id]) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
  };

  const handleAssignTo = async (record, assignedTo, assignedToName) => {
    if (!canAssign || assignInFlightRef.current.has(record.id)) {
      return { success: false };
    }
    assignInFlightRef.current.add(record.id);
    const response = await updateEsvRetestActionDataOps({
      id: record.id,
      user_id: getUser()?.id,
      assigned_to: assignedTo ?? null,
    });
    assignInFlightRef.current.delete(record.id);
    if (!isMountedRef.current) {
      return { success: response.success !== false && !response.canceled };
    }
    if (response.canceled || response.success === false) {
      toast.error(response.message || "Failed to update assignee");
      return { success: false };
    }
    setRows((currentRows) => currentRows.map((row) => (
      row.id === record.id
        ? mergeEsvAssignedToRow(row, assignedTo, assignedToName, response)
        : row
    )));
    return { success: true };
  };

  const handleDataOpsStatus = async (record, dataopsStatus) => {
    if (!canEditStatus || statusInFlightRef.current.has(record.id)) {
      return { success: false };
    }
    trackFieldSaving(statusInFlightRef, setSavingStatusIds, record.id, true);
    actionRefreshRef.current = true;
    setActionLoading(true);
    const response = await updateEsvRetestActionDataOps({
      id: record.id,
      user_id: getUser()?.id,
      dataops_status: dataopsStatus,
    });
    if (!isMountedRef.current) {
      return { success: response.success !== false && !response.canceled };
    }
    if (response.canceled || response.success === false) {
      trackFieldSaving(statusInFlightRef, setSavingStatusIds, record.id, false);
      actionRefreshRef.current = false;
      setActionLoading(false);
      toast.error(response.message || "Failed to update Data Ops status");
      return { success: false };
    }
    setListVersion((version) => version + 1);
    return { success: true };
  };

  const handleCustomerChange = (value) => {
    setCustomerPk(value ?? "");
    setPage(1);
  };

  const handleCsmChange = (value) => {
    setCsm(value ?? "");
    setPage(1);
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value || getDefaultDateRange());
    setPage(1);
  };

  const handlePageChange = (nextPage, nextPageSize) => {
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize);
      setPage(1);
      return;
    }
    setPage(nextPage);
  };

  const handleToggleRead = async (record) => {
    if (!canEditReadStatus || readInFlightRef.current || markingBulk) return;
    readInFlightRef.current = true;
    const markRead = !isActionRead(record.is_read);
    const userId = getUser()?.id;
    setMarkingId(record.id);
    actionRefreshRef.current = true;
    setActionLoading(true);
    const response = await markEsvRetestActionRead({
      id: record.id,
      user_id: userId,
      is_read: markRead ? 1 : 0,
    });
    readInFlightRef.current = false;
    if (!isMountedRef.current) return;
    if (response.canceled || response.success === false) {
      setMarkingId(null);
      actionRefreshRef.current = false;
      setActionLoading(false);
      toast.error(response.message || "Failed to update read status");
      return;
    }
    setListVersion((version) => version + 1);
  };

  const handleBulkRead = async (markRead) => {
    const targets = rows.filter((row) => (
      selectedRowKeys.includes(row.id) && isActionRead(row.is_read) !== markRead
    ));
    if (!canEditReadStatus || !targets.length || readInFlightRef.current || markingBulk) return;
    readInFlightRef.current = true;
    setMarkingBulk(true);
    actionRefreshRef.current = true;
    setActionLoading(true);
    const userId = getUser()?.id;
    const selectedIds = targets.map((row) => row.id);
    const response = await markEsvRetestActionRead({
      id: selectedIds,
      user_id: userId,
      is_read: markRead ? 1 : 0,
    });
    readInFlightRef.current = false;
    if (!isMountedRef.current) return;
    if (response.canceled || response.success === false) {
      setMarkingBulk(false);
      actionRefreshRef.current = false;
      setActionLoading(false);
      toast.error(response.message || "Failed to update read status");
      return;
    }
    setListVersion((version) => version + 1);
  };

  const handleBulkDataOps = async (dataopsStatus) => {
    const targets = rows.filter((row) => (
      selectedRowKeys.includes(row.id)
      && normalizeEsvClearance(row.dataops_status ?? row.clearance) !== dataopsStatus
    ));
    if (!canEditStatus || !targets.length || readInFlightRef.current || markingBulk) return;
    readInFlightRef.current = true;
    setMarkingBulk(true);
    actionRefreshRef.current = true;
    setActionLoading(true);
    const response = await updateEsvRetestActionDataOps({
      id: targets.map((row) => row.id),
      user_id: getUser()?.id,
      dataops_status: dataopsStatus,
    });
    readInFlightRef.current = false;
    if (!isMountedRef.current) return;
    if (response.canceled || response.success === false) {
      setMarkingBulk(false);
      actionRefreshRef.current = false;
      setActionLoading(false);
      toast.error(response.message || "Failed to update Data Ops status");
      return;
    }
    setListVersion((version) => version + 1);
  };

  const selectedRows = rows.filter((row) => selectedRowKeys.includes(row.id));
  const marking = markingId != null || markingBulk;

  const columns = [
    {
      title: "Customer #",
      dataIndex: "customer_id",
      key: "customer_id",
      width: 130,
      render: displayCell,
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      width: 170,
      render: displayCell,
    },
    {
      title: "WO ID",
      dataIndex: "workorder_id",
      key: "workorder_id",
      width: 120,
      render: displayCell,
    },
    {
      title: "Genista ID",
      dataIndex: "genista_id",
      key: "genista_id",
      width: 150,
      render: displayCell,
    },
    {
      title: "Test",
      dataIndex: "test",
      key: "test",
      width: 180,
      render: displayCell,
    },
    {
      title: "Result",
      dataIndex: "result",
      key: "result",
      width: 100,
      render: displayCell,
    },
    {
      title: "Action ESV",
      dataIndex: "action_label",
      key: "action_esv",
      width: 150,
      render: (_, record) => withTooltip(actionLabelFor(record), formatActionAt(record.action_at)),
    },
    {
      title: "Assign To",
      key: "assigned_to",
      width: 200,
      render: (_, record) => (
        <EsvAssignedToCell
          record={record}
          canEdit={canAssign}
          assigneeOptions={assigneeOptions}
          assigneeLoading={assigneeLoading}
          userNames={userNames}
          onChange={handleAssignTo}
        />
      ),
    },
    {
      title: "Data Ops Status",
      key: "dataops_status",
      width: 150,
      render: (_, record) => (
        <EsvDataOpsStatusCell record={record} />
      ),
    },
    {
      title: "Status",
      dataIndex: "is_read",
      key: "is_read",
      width: 100,
      render: (_, record) => (
        <Tooltip title={readStatusTooltip(record, userNames)}>
          <span>
            <EsvReadStatusCell record={record} />
          </span>
        </Tooltip>
      ),
    },
    ...(canEditReadStatus || canEditStatus ? [{
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      className: "EsvRetestActions__ActionsColumn",
      render: (_, record) => (
        <EsvRowActions
          record={record}
          loading={markingId === record.id || Boolean(savingStatusIds[record.id])}
          onMarkRead={handleToggleRead}
          onMarkDataOps={handleDataOpsStatus}
        />
      ),
    }] : []),
  ];

  const scrollX = columns.reduce((sum, column) => sum + column.width, 0) + 48;

  return (
    <div className="QCContainer DashboardMlRouting EsvRetestActionsDashboard" ref={containerRef}>
      <Space className="QCFilterBar" wrap>
        <div className="field-container">
          <label>Search</label>
          <Input.Search
            allowClear
            placeholder="Search by WO, Genista ID, Test"
            className="QCFilterSearch"
            value={searchDraft}
            onChange={(event) => {
              const nextValue = event.target.value;
              setSearchDraft(nextValue);
              if (nextValue === "") {
                applySearch("");
              }
            }}
            onSearch={applySearch}
          />
        </div>
        <div className="field-container">
          <label>Workorder Date Range</label>
          <DatePicker.RangePicker
            allowClear={false}
            className="QCFilterDate"
            format={dateFormat}
            value={dateRange}
            onChange={handleDateRangeChange}
          />
        </div>
        <div className="field-container">
          <label>Customer</label>
          <Select
            allowClear
            showSearch
            className="QCFilterBarCategory"
            popupClassName="QCFilterBarCategory__Menu"
            loading={customerLoading}
            options={customerOptions}
            value={customerPk}
            onSelect={handleCustomerChange}
            onClear={() => handleCustomerChange("")}
            filterOption={(inputValue, option) => option.label?.toLowerCase().includes(inputValue?.toLowerCase())}
            listHeight={400}
          />
        </div>
        <div className="field-container">
          <label>CSM</label>
          <Select
            allowClear
            showSearch
            className="QCFilterBarCategory"
            popupClassName="QCFilterBarCategory__Menu"
            loading={csmLoading}
            options={csmOptions}
            value={csm}
            onSelect={handleCsmChange}
            onClear={() => handleCsmChange("")}
            filterOption={(inputValue, option) => option.label?.toLowerCase().includes(inputValue?.toLowerCase())}
            listHeight={400}
          />
        </div>
        <div className="field-container">
          <label>Status</label>
          <Select
            allowClear
            className="QCFilterBarCategory"
            popupClassName="QCFilterBarCategory__Menu"
            options={READ_STATUS_OPTIONS}
            value={readStatus}
            onSelect={handleReadStatusChange}
            onClear={() => handleReadStatusChange("")}
            listHeight={400}
          />
        </div>
        <div className="field-container">
          <label>Data Ops Status</label>
          <Select
            className="QCFilterBarCategory"
            popupClassName="QCFilterBarCategory__Menu"
            options={ESV_CLEARANCE_FILTER_OPTIONS}
            value={clearanceFilter}
            onChange={handleClearanceFilterChange}
            listHeight={400}
          />
        </div>
        <EsvBulkActions
          selectedCount={selectedRows.length}
          loading={markingBulk}
          canMarkRead={selectedRows.some((row) => !isActionRead(row.is_read))}
          canMarkUnread={selectedRows.some((row) => isActionRead(row.is_read))}
          canMarkPending={selectedRows.some((row) => normalizeEsvClearance(row.dataops_status ?? row.clearance) === "cleared")}
          canMarkCleared={selectedRows.some((row) => normalizeEsvClearance(row.dataops_status ?? row.clearance) === "pending")}
          onMarkRead={handleBulkRead}
          onMarkDataOps={handleBulkDataOps}
        />
      </Space>
      <div style={{ width: tableWidth }}>
        <Table
          rowKey={(record) => record.id}
          bordered
          sticky
          className="EsvRetestActionsDashboard__Table"
          columns={columns}
          dataSource={rows}
          loading={!actionLoading && loading}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            columnWidth: 48,
            getCheckboxProps: () => ({
              disabled: marking,
            }),
          }}
          tableLayout="fixed"
          scroll={{ x: scrollX, y: "60vh" }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "50", "100"],
            onChange: handlePageChange,
          }}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No ESV retest actions" />,
          }}
          onRow={(record) => ({
            onClick: (event) => {
              if (event.target.closest(".ant-table-selection-column, .ant-checkbox-wrapper, .ant-checkbox, button, .ant-btn, .ant-select, .ant-radio-group, .ant-radio-button-wrapper, .ant-spin")) {
                return;
              }
              setDetailId(record.id);
            },
            style: { cursor: "pointer" },
          })}
        />
      </div>
      <EsvActionDetailModal
        open={detailId != null}
        record={rows.find((row) => row.id === detailId) || null}
        userNames={userNames}
        markingId={markingId}
        onToggleRead={handleToggleRead}
        assigneeOptions={assigneeOptions}
        assigneeLoading={assigneeLoading}
        savingStatusIds={savingStatusIds}
        onAssignTo={handleAssignTo}
        onDataOpsStatus={handleDataOpsStatus}
        onCancel={() => setDetailId(null)}
      />
    </div>
  );
}
